/** @file Apply roster of a team to home-side tokens. */

/** @typedef {import("./play.js").Token} Token */
/** @typedef {import("../types/schemas.js").TeamDoc} TeamDoc */

/**
 * Returns a new tokens array where home-side tokens are filled from the roster.
 * Position-hint (roster.position) preferred match by label; otherwise first-come-first-serve
 * against home tokens sorted by current `number` asc.
 * @param {Token[]} tokens
 * @param {TeamDoc} team
 * @returns {Token[]}
 */
export function applyTeamToTokens(tokens, team) {
  const result = tokens.map((t) => ({ ...t }));
  const homeIdx = result
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => t.team === "home")
    .sort((a, b) => a.t.number - b.t.number)
    .map(({ i }) => i);

  /** @type {Set<number>} */
  const assigned = new Set();
  /** @type {import("../types/schemas.js").RosterPlayer[]} */
  const remaining = [];

  // First pass: match by position hint.
  for (const player of team.roster) {
    if (!player.position) {
      remaining.push(player);
      continue;
    }
    const slot = homeIdx.find(
      (idx) => !assigned.has(idx) && result[idx]?.label === player.position,
    );
    if (slot !== undefined) {
      const tok = result[slot];
      if (tok) {
        tok.number = player.number;
        tok.color = team.color;
      }
      assigned.add(slot);
    } else {
      remaining.push(player);
    }
  }

  // Second pass: fill remaining in order.
  for (const player of remaining) {
    const slot = homeIdx.find((idx) => !assigned.has(idx));
    if (slot === undefined) {
      break;
    }
    const tok = result[slot];
    if (tok) {
      tok.number = player.number;
      tok.color = team.color;
    }
    assigned.add(slot);
  }

  return result;
}
